import React from "react";
import "./SharePlanCommission.scss";
import SubHeader from "../../../../components/subheader/SubHeader";
import TotalYearCard from "../../../../components/totalrevenue/totalyearcard/TotalYearCard";
import TotalQuarterCard from "../../../../components/totalrevenue/totalquarterard/TotalQuarterCard";
import TotalMonthCard from "../../../../components/totalrevenue/totalmonthcard/TotalMonthCard";
import RevenueLineChart from "../../../../components/revenue/revenuechart/RevenueLineChart";
import RadialChart from "../../../../components/cost/radialchart/RadialChart";
import RevenueByStateMap from "../../../../components/revenue/revenuebystatemap/RevenueByStateMap";
import RevenueBarChart from "../../../../components/revenue/revenuebarchart/RevenueBarChart";
import RevenueTableCategories from "../../../../components/revenue/revenuetablecategories/RevenueTableCategories";
import { AppContext } from "../../../../context/Context";
import { useContext } from "react";
import { getQuarterByMonth, createRevenueDateTitle, createRevenueCardYearTitle, createRevenueCardQuarterTitle, createRevenueCardMonthTitle } from "../../../../utils/format";
const SharePlanCommission = () => {
  const { data, fromDate, toDate } = useContext(AppContext);

  const shortcutimport = data.cost?.commisson.shareplan;

  const allData = data.cost?.commisson.shareplan.costshareplantotalCommission || [];

  const firstRevenueYearMonth =
        allData.length > 0 ? allData[0].year_month : null;
    
  const lastRevenueYearMonth =
    allData.length > 0 ? allData[allData.length - 1].year_month : null;

  const computeAgentCommissionYear= (fromDate, toDate, data) => {
    let total = 0;

    if (fromDate === "All" || !toDate) {
      total =
        data.cost?.commisson.shareplan.costshareplantotalCommission?.reduce(
          (sum, item) => sum + (item.agent_commission || 0),
          0
        ) || 0;
    } else {
      const fromYear = parseInt(fromDate.split("-")[0]);
      const toYear = parseInt(toDate.split("-")[0]);

      total =
        data.cost?.commisson.shareplan.costshareplantotalCommission
          ?.filter((item) => {
            const itemYear = parseInt(item.year_month.split("-")[0]);
            return itemYear >= fromYear && itemYear <= toYear;
          })
          .reduce((sum, item) => sum + (item.agent_commission || 0), 0) || 0;
    }

    // Format with commas and 2 decimal places
    return total;
  };

  const computeAgentCommissionQuarter = (fromDate, toDate, data) => {
    if (!data.cost?.commisson.shareplan.costshareplantotalCommission) return 0;

    // Handle "All"
    if (fromDate === "All" || !toDate) {
      return data.cost?.commisson.shareplan.costshareplantotalCommission.reduce(
        (sum, item) => sum + (item.agent_commission || 0),
        0
      );
    }

    // --- Parse from/to year & month ---
    const [fromYear, fromMonth] = fromDate.split("-").map(Number);
    const [toYear, toMonth] = toDate.split("-").map(Number);

    const fromQuarter = getQuarterByMonth(fromMonth);
    const toQuarter = getQuarterByMonth(toMonth);

    // --- Filter ---
    const total = data.cost?.commisson.shareplan.costshareplantotalCommission
      .filter((item) => {
        const [itemYear, itemMonth] = item.year_month.split("-").map(Number);
        const itemQuarter = getQuarterByMonth(itemMonth);

        // year before range
        if (itemYear < fromYear || itemYear > toYear) return false;

        // same year as start
        if (itemYear === fromYear && itemQuarter < fromQuarter) return false;

        // same year as end
        if (itemYear === toYear && itemQuarter > toQuarter) return false;

        // in between years → always included
        return true;
      })
      .reduce((sum, item) => sum + (item.agent_commission || 0), 0);

    return total;
  };

  const computeAgentCommissionCurrentMonth = (data) => {
    const lines = data.cost?.commisson.shareplan.costshareplantotalinecommission;
    if (!Array.isArray(lines)) return "0.00";

    const total = lines.reduce(
      (sum, item) => sum + (item.agent_commission || 0),
      0
    );

    return total;
  };

  const computeAgentCommissionPreviousMonth = (data) => {
    if (!data.cost?.commisson.shareplan.costshareplanpreviousmonth) return 0;
    return data.cost?.commisson.shareplan.costshareplanpreviousmonth.reduce(
      (sum, item) => sum + item.agent_commission,
      0
    );
  }

  const renameKeys = (data) => {
    if (!Array.isArray(data)) {
      console.error("renameKeys: data is not an array or is undefined", data);
      return [];
    }
    return data.map((item) => ({
      month: item.year_month,
      value: item.agent_commission,
    }));
  };

  const groupedRegional = shortcutimport.costshareplanregion.reduce(
    (acc, item) => {
      const { regional, agent_commission } = item;
      if (!acc[regional]) {
        acc[regional] = 0;
      }
      acc[regional] += agent_commission;
      return acc;
    },
    {}
  );
  const staticData = [
    { name: "Region 1", fill: "url(#color1)" },
    { name: "Region 3", fill: "url(#color3)" },
    { name: "Region 2", fill: "url(#color2)" },
    { name: "(None)", fill: "url(#color4)" },
  ];

  const resultArray = Object.entries(groupedRegional)
    .map(([type, total]) => {
      const matchedStatic = staticData.find((item) => item.name === type);
      return {
        name: type,
        value: total,
        fill: matchedStatic ? matchedStatic.fill : undefined,
      };
    })
    .sort((a, b) => a.value - b.value);

  const sumByState = (data) => {
    if (!Array.isArray(data)) {
      console.error("Invalid data: Expected an array but got", data);
      return {};
    } 
    return data.reduce((acc, item) => {
      acc[item.state] =
        (acc[item.state] || 0) + item.agent_commission;
      return acc;
    }, {});
  };

  const sumState = sumByState(shortcutimport.costshareplanstates);

  const HighestStateArray = Object.entries(sumState)
    .map(([state, revenue]) => ({
      state,
      revenue: parseFloat(revenue.toFixed(2)),
    }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);

  const sumByMarket = (data) => {
    if (!Array.isArray(data)) {
      console.error("Invalid data: Expected an array but got", data);
      return {};
    }
    return data.reduce((acc, item) => {
      acc[item.market] =
        (acc[item.market] || 0) + item.agent_commission;
      return acc;
    }, {});
  };

  const sumMarket = sumByMarket(shortcutimport.costshareplanmarket);

  const HighestMarketArray = Object.entries(sumMarket)
    .map(([state, revenue]) => ({
      state,
      revenue: parseFloat(revenue.toFixed(2)),
    }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10);

  // Step 1: Find the latest and previous months dynamically
  const uniqueMonths = [
    ...new Set(
      data.cost?.commisson?.shareplan?.costshareplancategoriesaccountrename.map(
        (item) => item.year_month
      )
    ),
  ].sort();

  const previousMonths = uniqueMonths[uniqueMonths.length - 2];

  // Step 2: Create a map of last month's revenue income
  const lastMonthData = {};
  data.cost?.commisson.shareplan.costshareplancategoriesaccountrename.forEach(
    (item) => {
      if (item.year_month === previousMonths) {
        lastMonthData[item.account_dashboard_rename] = item.goods_sold_cost;
      }
    }
  );

  const transformedData = data.cost?.commisson.shareplan.costshareplancategoriesaccountrename
    // .filter((item) => item.year_month === latestMonth)
    .map((item) => {
      const lastPeriodValue = data.cost?.commisson.shareplan.costshareplancategoriesaccountrename
      .filter(
        (x) =>
          x.account_dashboard_rename === item.account_dashboard_rename &&
          x.range_type === "previous"
      )
      .reduce((sum, x) => sum + (x.goods_sold_cost || 0), 0);
      const currentValue = data.cost?.commisson.shareplan.costshareplancategoriesaccountrename
      .filter(
        (x) =>
          x.account_dashboard_rename === item.account_dashboard_rename &&
          x.range_type === "current"
      )
      .reduce((sum, x) => sum + (x.goods_sold_cost || 0), 0);
      
      // Safe percent change
      let percentChange = 0;
      if (lastPeriodValue === 0) {
        percentChange = currentValue > 0 ? 100 : 0;
      } else {
        percentChange = ((currentValue - lastPeriodValue) / lastPeriodValue) * 100;
      }

      return {
        account_dashboard_rename: item.account_dashboard_rename,
        revenue_income: currentValue,
        revenue_income_lastmonth: lastPeriodValue,
        percentChange,
      };
    })
    // prevent duplicates (optional)
    .filter(
      (v, i, a) =>
        a.findIndex(
          (t) => t.account_dashboard_rename === v.account_dashboard_rename
        ) === i
    )
    .sort((a, b) => b.revenue_income - a.revenue_income);

  return (
    <div className="frame-main-cost-commission">
      <SubHeader title="COST OF GOODS SOLD (AGENT'S COMMISSION - SHARE PLAN)" />
      <div className="frame-cost-commission-block1">
        <div className="frame-cost-commission-block1-container">
          <div className="frame-commission-block1-top">
            <div className="frame-block1-shareplan-top">
              <TotalYearCard
                title={createRevenueCardYearTitle(firstRevenueYearMonth, lastRevenueYearMonth, fromDate, toDate)}
                amount={computeAgentCommissionYear(fromDate, toDate, data)}
              />
            </div>
            <div className="frame-block2-shareplan-top">
              <TotalQuarterCard
                title={createRevenueCardQuarterTitle(firstRevenueYearMonth, lastRevenueYearMonth, fromDate, toDate)}
                amount={computeAgentCommissionQuarter(fromDate, toDate, data)}
              />
            </div>

            <div className="frame-block3-shareplan-top">
              <TotalMonthCard
                title={createRevenueCardMonthTitle(firstRevenueYearMonth, lastRevenueYearMonth, fromDate, toDate)}
                amount={computeAgentCommissionCurrentMonth(data)}
                previousMonthAmount={computeAgentCommissionPreviousMonth(data)}
              />
            </div>
          </div>
          <div className="frame-block1-overall-left-bottom">
            <RevenueLineChart
              color="#4766D9"
              data={renameKeys(shortcutimport.costshareplantotalCommission)}
            />
          </div>
        </div>
      </div>

      <div className="frame-cost-commission-block2">
        <div className="frame-cost-commission-block2-left">
          <h3>TOTAL COST OF GOODS SOLD BY REGIONAL</h3>
          <RadialChart data={resultArray} />
        </div>
        <div className="frame-cost-commission-block2-right">
          <h3>TOP 5 STATES WITH HIGHEST COGS</h3>
          <div className="frame-cost-commission-block2-bottom">
            <div className="frame-cost-commission-block2-bottom-left">
              <RevenueByStateMap
                revenueData={sumState}
                colors={[
                  "#E7DFF7",
                  "#D0D1F2",
                  "#B9C3ED",
                  "#A1B5E8",
                  "#8AA7E3",
                  "#7B98F0",
                  "#6B89F7",
                ]}
              />
            </div>
            <div className="frame-cost-commission-block2-bottom-right">
              <RevenueBarChart
                height={400}
                firstColor="#E7DFF7"
                secondColor="#6B89F7"
                data={HighestStateArray}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="frame-cost-commission-block3">
        <div className="frame-cost-commission-block3-left">
          <h3>TOP 10 MARKETS WITH HIGHEST COGS</h3>
          <RevenueBarChart
            height={750}
            firstColor="#E7DFF7"
            secondColor="#6B89F7"
            data={HighestMarketArray}
          />
        </div>
        <div className="frame-cost-commission-block3-right">
          <h3>TOTAL COST OF GOODS SOLD BY CATEGORIES ({createRevenueDateTitle(firstRevenueYearMonth, lastRevenueYearMonth, fromDate, toDate)})</h3>
          <RevenueTableCategories
            data={transformedData}
             from_date={fromDate}
            headerTitle={[
              "Categories",
              "Total COGS",
              "Total COGS (Last month)",
              "% Change",
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default SharePlanCommission;
